import { axiosConfig } from "@/services/axiosConfig";
import {
  buildPageParams,
  extractList,
  extractPaginationMeta,
  type PaginatedResult,
} from "@/projects/lib/pagination";

const TOPICS_PATH = "/rajnagar_gyanmahostav/topics/";

/** Raw API record — fields may vary slightly; mapper is defensive. */
export type ApiTopic = {
  id?: string | number;
  pk?: string | number;
  name?: string;
  title?: string;
  min_age?: number | string | null;
  max_age?: number | string | null;
  /** Max video upload size in MB */
  size?: number | string | null;
  start_time?: string | null;
  end_time?: string | null;
  is_active?: boolean;
  active?: boolean;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

export type Topic = {
  id: string;
  name: string;
  minAge: number | null;
  maxAge: number | null;
  /** Max video upload size in MB */
  size: number | null;
  startTime: string | null;
  endTime: string | null;
  isActive: boolean;
};

export type TopicWritePayload = {
  name: string;
  min_age?: number;
  max_age?: number;
  /** Max video upload size in MB */
  size?: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
};

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const n = typeof value === "number" ? value : Number(value);
  return Number.isNaN(n) ? null : n;
}

export function mapApiTopicToTopic(row: ApiTopic): Topic {
  const id = String(row.id ?? row.pk ?? row.name ?? "");
  const active =
    typeof row.is_active === "boolean"
      ? row.is_active
      : typeof row.active === "boolean"
        ? row.active
        : true;
  return {
    id,
    name: row.name?.trim() || row.title?.trim() || "—",
    minAge: toNumberOrNull(row.min_age),
    maxAge: toNumberOrNull(row.max_age),
    size: toNumberOrNull(row.size),
    startTime: row.start_time ?? null,
    endTime: row.end_time ?? null,
    isActive: active,
  };
}

function detailPath(id: string) {
  return `${TOPICS_PATH}${encodeURIComponent(id)}/`;
}

function unwrapOne(payload: ApiTopic | { data?: ApiTopic }): ApiTopic {
  if (payload && typeof payload === "object" && "data" in payload && payload.data) {
    return payload.data as ApiTopic;
  }
  return payload as ApiTopic;
}

export async function fetchTopicsPage(params?: {
  page?: number;
  pageSize?: number;
  age?: number;
  isActive?: boolean;
}): Promise<PaginatedResult<Topic>> {
  const query: Record<string, string> = {
    ...buildPageParams(params?.page, params?.pageSize),
  };
  if (params?.age !== undefined && !Number.isNaN(params.age)) {
    query.age = String(params.age);
  }
  if (params?.isActive !== undefined) {
    query.is_active = String(params.isActive);
  }
  const { data } = await axiosConfig.get(TOPICS_PATH, {
    params: query,
    headers: { Accept: "application/json" },
  });
  const list = extractList<ApiTopic>(data);
  const items = list.map(item => mapApiTopicToTopic((item ?? {}) as ApiTopic));
  return {
    items,
    pagination: extractPaginationMeta(data, items.length, params?.page ?? 1),
  };
}

export async function fetchTopics(params?: {
  age?: number;
  isActive?: boolean;
}): Promise<Topic[]> {
  const { items } = await fetchTopicsPage({ ...params, pageSize: 1000 });
  return items;
}

export async function createTopic(payload: TopicWritePayload): Promise<Topic> {
  const { data } = await axiosConfig.post<ApiTopic | { data: ApiTopic }>(
    TOPICS_PATH,
    payload,
    { headers: { "Content-Type": "application/json", Accept: "application/json" } }
  );
  return mapApiTopicToTopic(unwrapOne(data));
}

export async function updateTopic(
  id: string,
  payload: Partial<TopicWritePayload>
): Promise<Topic> {
  const { data } = await axiosConfig.patch<ApiTopic | { data: ApiTopic }>(
    detailPath(id),
    payload,
    { headers: { "Content-Type": "application/json", Accept: "application/json" } }
  );
  return mapApiTopicToTopic(unwrapOne(data));
}

export async function setTopicActive(
  id: string,
  isActive: boolean
): Promise<Topic> {
  return updateTopic(id, { is_active: isActive });
}

export async function deleteTopic(id: string): Promise<void> {
  await axiosConfig.delete(detailPath(id), {
    headers: { Accept: "application/json" },
  });
}

export function getTopicApiErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) {
    return err?.message || fallback;
  }
  for (const key of ["message", "detail", "error", "non_field_errors"]) {
    const v = data[key];
    if (typeof v === "string" && v.trim()) {
      return v;
    }
    if (Array.isArray(v) && typeof v[0] === "string") {
      return v[0];
    }
  }
  for (const [k, v] of Object.entries(data)) {
    if (Array.isArray(v) && typeof v[0] === "string") {
      return `${k}: ${v[0]}`;
    }
    if (typeof v === "string" && v.trim() && k !== "status" && k !== "success") {
      return v;
    }
  }
  return fallback;
}

/** ISO datetime → value for <input type="datetime-local"> in local time. */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value → ISO string (UTC, with Z). */
export function localInputToIso(local: string): string {
  if (!local) {
    return "";
  }
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  return d.toISOString();
}
